import React, { useState, useEffect, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import { Modal } from 'bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Typeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import appointmentStyles from "../../../styles/userDetails/appointmentScheduler.module.css";
import { useNavigate } from 'react-router-dom';
import ViewBranchModal from "./viewBranchModal";
import EditBranchDetailsModal from "./editBranchDetailsModal";
import apptDashboardDataClient from '../../../utility/features/api_clients/appointment/getApptDashboardData'
import getBranchesList from '../../../utility/features/api_clients/reports/getBranchesList';
import TimeBlocks from '../timeblocks';
import HolidaysList from '../holidaysList';

const AppointmentScheduler = () => {
    const navigate = useNavigate();
    const [appointments, setAppointments] = useState([]);
    const [errorMessageRes, setErrorMessageRes] = useState("");
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedBranch, setSelectedBranch] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loadingBranches, setLoadingBranches] = useState(true);
    const [hasSearched, setHasSearched] = useState(false);
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [editBranch, setEditBranch] = useState(null);
    const [isBranchEdited, setIsBranchEdited] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(false);
    const [successMessage, setSuccessMessage] = useState(false);
    const [branchDetails, setBranchDetails] = useState({});
    const [showTimeBlocks, setShowTimeBlocks] = useState(false);
    const [selectedBranchForSlots, setSelectedBranchForSlots] = useState(null);
    const [showHolidays, setShowHolidays] = useState(false);

    // Reset sub-views every time the Appointment Schedule tab becomes visible
    useEffect(() => {
        setShowHolidays(false);
        setShowTimeBlocks(false);
        setSelectedBranchForSlots(null);

        // Use MutationObserver to detect when the tab-pane becomes visible
        const tabPaneId = 'appointmentSchedule';
        const tabPane = document.getElementById(tabPaneId);
        let observer;
        if (tabPane) {
            observer = new MutationObserver(() => {
                if (tabPane.classList.contains('active') && tabPane.classList.contains('show')) {
                    setShowHolidays(false);
                    setShowTimeBlocks(false);
                    setSelectedBranchForSlots(null);
                }
            });
            observer.observe(tabPane, { attributes: true, attributeFilter: ['class'] });
        }
        return () => {
            if (observer) observer.disconnect();
        };
    }, []);


    const successModalRef = useRef(null);
    const failureModalRef = useRef(null);
    // Calculate max date for end date (7 days from start date)
    const getMaxEndDate = () => {
        if (!startDate) return null;
        const maxDate = new Date(startDate);
        maxDate.setDate(maxDate.getDate() + 7);
        return maxDate;
    };

    // Filter dates for end date picker
    const filterEndDate = (date) => {
        if (!startDate) return false;
        const maxDate = getMaxEndDate();
        return date > startDate && date <= maxDate;
    };

    // Handle start date change
    const handleStartDateChange = (date) => {
        setStartDate(date);
        // Reset end date if it's outside the new valid range
        if (endDate) {
            const maxDate = new Date(date);
            maxDate.setDate(maxDate.getDate() + 7);
            if (endDate <= date || endDate > maxDate) {
                setEndDate(null);
            }
        }
    };

    const fetchBranchDashBoardData = async (branchId = null) => {
        setErrorMessageRes("");
        setLoadingSearch(true);

        try {
            const result = await apptDashboardDataClient(branchId);
            if (result ?.status !== 200) {
                setErrorMessageRes("Appointment Scheduler dashboard data fetch failed. Please try again.");
                return;
            }

            const { status, data } = result ?? {};

            if (status === 200 && data ?.messageCode !== 20000) {
                setAppointments(data);
            } else if (data.messageCode === 20000) {
                setErrorMessageRes(data.message || "No Appointment Data Available for the selected criteria.");
                setAppointments([]);
                console.log("Error calling appointment scheduler dashboad data api :", data.message);
            } else {
                setErrorMessageRes(data.message || "Unexpected response from server.");
            }
        }
        catch (err) {
            console.error("Error logging in:", err);
        } finally {
            setLoadingSearch(false);
        }
    }

    // Fetch branches on component mount
    useEffect(() => {
        const fetchBranches = async () => {
            try {
                setLoadingBranches(true);
                const response = await getBranchesList();
                if (response && !response.error && response.data) {
                    setBranches(response.data);
                } else {
                    console.error('Failed to fetch branches:', response?.message);
                    setBranches([]);
                }
            } catch (error) {
                console.error('Error fetching branches:', error);
                setBranches([]);
            } finally {
                setLoadingBranches(false);
            }
        };

        fetchBranches();
    }, []);

    // Fetch all branch dashboard data on component mount
    useEffect(() => {
        fetchBranchDashBoardData();
    }, [isBranchEdited]);

    // No client-side filtering since data is fetched from API based on branch ID
    const filteredBranchSummary = appointments;

    const [activePage, setActivePage] = useState(1);
    const ITEMS_PER_PAGE = 10;
    const totalPages = Math.ceil(filteredBranchSummary?.length / ITEMS_PER_PAGE);
    const startIndex = (activePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const paginatedAppointments = filteredBranchSummary?.slice(startIndex, endIndex);

    const handlePageClick = (pageNum) => {
        setActivePage(pageNum);
    };

    const handleSearch = async () => {
        if (!searchTerm) {
            alert('Please select a branch');
            return;
        }
        setActivePage(1); // Reset to first page on search
        setHasSearched(true);
        await fetchBranchDashBoardData(searchTerm);
    };

    const handleHolidaysClick = () => {
        setShowHolidays(true);
    };

    const handleBackFromHolidays = () => {
        setShowHolidays(false);
    };


    const handleViewBranchModal = (branch) => {
        setBranchDetails(branch)
    };

    const handleUpdateSlotsClick = (branch) => {
        setSelectedBranchForSlots(branch);
        setShowTimeBlocks(true);
    };

    const handleBackToList = () => {
        setShowTimeBlocks(false);
        setSelectedBranchForSlots(null);
    };
    const openSuccessModal = () => {
        if (successModalRef.current) { 
           const instance = Modal.getOrCreateInstance(successModalRef.current); 
           instance.show(); 
           } 
       };
   
      const openFailureModal = () => {
        if (failureModalRef.current) { 
           const instance = Modal.getOrCreateInstance(failureModalRef.current); 
           instance.show(); 
           } 
       };

    return (
        <div className="container p-0">
            {showHolidays ? (
                <div>
                    <HolidaysList handleBackFromHolidays={handleBackFromHolidays} />
                </div>
            ) : showTimeBlocks ? (
                <div>
                    <button
                        type="button"
                        className={`btn btn-secondary mb-3`}
                        onClick={handleBackToList}
                    >
                        &larr; Back to Branch List
                    </button>
                    <TimeBlocks branchID={selectedBranchForSlots?.branchID} branchName={selectedBranchForSlots?.branchName} />
                </div>
            ) : (
            <>
            <div className='row py-2'>
                <div className='col-12 col-md-3 mb-2 mb-md-0'>
                    <label className='form-label'>Search by Branch</label>
                    <div className="">
                        <Typeahead 
                            id="branch-typeahead"
                            labelKey={(option) => `${option.branchID || option.id}${option.branchName ? ` - ${option.branchName}` : ''}`}
                            options={branches}
                            placeholder={loadingBranches ? 'Loading branches...' : 'Select or search branch...'}
                            selected={selectedBranch}
                            inputProps={{ maxLength: 4 }}
                            onChange={(selected) => {
                                setSelectedBranch(selected);
                                const branchId = selected.length > 0 ? String(selected[0].branchID || selected[0].id) : '';
                                setSearchTerm(branchId);
                                // If cleared, fetch all branch data
                                if (selected.length === 0) {
                                    fetchBranchDashBoardData();
                                }
                            }}
                            disabled={loadingBranches}
                            clearButton
                            emptyLabel="No branches found"
                            positionFixed
                        />
                    </div>
                </div>
                <div className='col-12 col-md-2 mb-2 mb-md-0'>
                    <label className='form-label'>&nbsp;</label>
                    <button
                        type="button"
                        className={`btn btn-primary w-100 ${appointmentStyles["bg-green"]}`}
                        onClick={handleSearch}
                    >
                        Search
                    </button>
                </div>
                <div className='col-12 col-md-2 mb-2 mb-md-0'>
                </div>
                <div className='col-12 col-md-2 mb-2 mb-md-0'>
                </div>
                
                <div className='col-12 col-md-3 mb-2 mb-md-0'>
                    <label className='form-label'>&nbsp;</label>
                    <button
                        type="button"
                        className={`btn btn-info w-100 ${appointmentStyles["bg-green"]}`}
                        onClick={handleHolidaysClick}
                    >
                        Holidays Details
                    </button>
                </div>
            </div>


            {/* </div> */}

            <div className={`mb-4 ${appointmentStyles["table-theme"]}`}>
                <div className={`table-responsive ${appointmentStyles["table-card"]}`}>
                    <table className={`w-100 ${appointmentStyles["table"]} align-middle`}>
                        <thead>
                            <tr>
                                <th scope="col">Branch Id</th>
                                <th scope="col">Branch Name</th>
                                <th scope="col">Available Slots</th>
                                <th scope="col">Status</th>
                                <th scope="col">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loadingSearch ? (
                                <tr>
                                    <td colSpan="8" className="text-center py-4">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <div className="mt-2">Loading data...</div>
                                    </td>
                                </tr>
                            ) : paginatedAppointments.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="text-center py-4">
                                        No data available
                                    </td>
                                </tr>
                            ) : (
                                paginatedAppointments.map((branch) => {
                                    return (
                                        <tr key={branch.branchID}>
                                            <td>{branch.branchID}</td>
                                            <td>{branch.branchName}</td>
                                            <td>{branch.availableSlots}</td>
                                            
                                             <td className={branch.isBranchEnabled === true ? "text-success" : "text-danger"}>
                                                {branch.isBranchEnabled === true ? "Active" : "In Active"}
                                            </td>
                                            <td>
                                                 <button className="btn btn-sm btn-outline-success rounded-pill py-1 px-3" type="button"
                                                        data-bs-toggle="modal" data-bs-target="#viewBranchModal" onClick={() => handleViewBranchModal(branch)}>
                                                        View
                                                    </button>
                                                &nbsp;&nbsp;
                                                 <button className="btn btn-sm btn-outline-success rounded-pill py-1 px-3" type="button"
                                                        data-bs-toggle="modal" data-bs-target="#editBranchModal" onClick={() => setEditBranch(branch)}>
                                                        Edit
                                                    </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {totalPages > 1 && (
                <div className={appointmentStyles['pagination-wrapper']}>
                    <div className={appointmentStyles['pagination']}>
                        <button 
                            className={`${appointmentStyles["page-btn"]} ${appointmentStyles['nav-btn']}`} 
                            onClick={() => handlePageClick(activePage - 1)}
                            disabled={activePage === 1}
                        >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>

                        {activePage > 3 && (
                            <>
                                <button className={appointmentStyles["page-btn"]} onClick={() => handlePageClick(1)}>1</button>
                                {activePage > 4 && <span className={appointmentStyles["ellipsis"]}>...</span>}
                            </>
                        )}

                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter((num) => num >= activePage - 2 && num <= activePage + 2)
                            .map((num) => (
                                <button 
                                    key={num} 
                                    className={`${appointmentStyles["page-btn"]} ${num === activePage ? appointmentStyles["active"] : ""}`} 
                                    onClick={() => handlePageClick(num)}
                                >
                                    {num}
                                </button>
                            ))}

                        {activePage < totalPages - 2 && (
                            <>
                                {activePage < totalPages - 3 && <span className={appointmentStyles["ellipsis"]}>...</span>}
                                <button className={appointmentStyles["page-btn"]} onClick={() => handlePageClick(totalPages)}>{totalPages}</button>
                            </>
                        )}

                        <button 
                            className={`${appointmentStyles["page-btn"]} ${appointmentStyles['nav-btn']}`} 
                            onClick={() => handlePageClick(activePage + 1)}
                            disabled={activePage === totalPages}
                        >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                    </div>

                    <div className={appointmentStyles['pagination-info']}>
                        <span>Showing {startIndex + 1} - {Math.min(endIndex, filteredBranchSummary?.length)} of {filteredBranchSummary?.length} branches</span>
                    </div>
                </div>
            )}
            
             <ViewBranchModal branchDetails={branchDetails}/>
             
            <EditBranchDetailsModal branchDetails={editBranch} setLoading={setLoading} setIsBranchEdited={setIsBranchEdited}
            successModalRef={successModalRef} failureModalRef={failureModalRef} 
              setSuccessMessage={setSuccessMessage} setErrorMessage={setErrorMessage}
            openSuccessModal={openSuccessModal} openFailureModal={openFailureModal}
            handleUpdateSlotsClick={handleUpdateSlotsClick}
            />
            </>
            )}
            
            {/** Success modal */}
            <div ref={successModalRef} className={`modal fade`} id="successModal" data-backdrop="static" data-keyboard="false">
                <div className={`modal-dialog modal-lg`}>
                    <div className={`modal-content`}>
                        <div className={`modal-header`}>
                            <h4 className={`modal-title text-center`}>{successMessage}</h4>
                            <button type="button" className={`btn-close `} data-dismiss="modal"  data-bs-dismiss="modal" ></button>
                        </div>
                        <div className={`modal-body`}>
                            <form className={``} noValidate="" id="leadSuccessForm" name="leadSuccessForm">
                                <div className="row mt-4">
                                    <div className="col-md-4">
                                        <button type="button" id="btnDone" data-dismiss="modal" data-bs-dismiss="modal" > Done</button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            {/** Failure modal */}

            <div ref={failureModalRef} className={`modal fade `} id="failureModal" data-backdrop="static" data-keyboard="false">
                <div className={`modal-dialog modal-lg`}>
                    <div className={`modal-content `}>
                        <div className={`modal-header`}>
                            <h4 className={`modal-title`}>Error updating branch details!</h4>
                            <button type="button" className={`btn-close`} data-dismiss="modal"  data-bs-dismiss="modal" ></button>
                        </div>
                        <div className={`modal-body`}>
                            <div className={``}>
                                <p>{errorMessage || "Error updating branch details!"}</p>
                            </div>
                            <form className={``} noValidate="" id="leadSuccessForm" name="leadSuccessForm">
                                <div className="row mt-4">
                                    <div className="col-md-4">
                                        <button type="button" id="btnDone" data-dismiss="modal" data-bs-dismiss="modal" > Done</button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
};


export default AppointmentScheduler;